import React, { useMemo } from 'react';
import { Select, Space, Typography, Alert, Spin } from 'antd';
import type { RoamingProfile } from '../../services/adminService';
import {
  groupRoamingProfiles,
  roamingLabel,
  roamingKey,
  parseRoamingKey,
} from '../../esim/bundleHelpers';
import CoverageList from './CoverageList';

const { Paragraph, Text } = Typography;

interface RoamingPickerProps {
  profiles: RoamingProfile[];
  loading?: boolean;
  value?: { group: string; id: number } | null;
  onChange: (v: { group: string; id: number } | null, profile: RoamingProfile | undefined) => void;
  // Suggested default (the bundle's product default), shown as a hint.
  recommended?: { group: string; id: number } | null;
  // Fixed-zone bundle: show the locked profile instead of a picker.
  locked?: { group: string; id: number; name?: string | null } | null;
}

// KAN-167: choose the roaming control profile ("coverage") for a bundle.
// Regional tiers are cumulative: tier 3 = zones 1, 2 and 3. Single-country
// profiles cover exactly one country.
const RoamingPicker: React.FC<RoamingPickerProps> = ({
  profiles,
  loading,
  value,
  onChange,
  recommended,
  locked,
}) => {
  const groups = useMemo(() => groupRoamingProfiles(profiles), [profiles]);
  const selected = value
    ? profiles.find(p => p.group === value.group && p.id === value.id)
    : undefined;

  if (locked) {
    return (
      <Space direction="vertical" style={{ width: '100%' }}>
        <Alert
          type="info"
          showIcon
          message="Coverage is fixed by this bundle"
          description={`${locked.name || `Roaming profile ${locked.id}`} in group "${locked.group}" is part of the product and cannot be changed.`}
        />
        <CoverageList group={locked.group} id={locked.id} />
      </Space>
    );
  }

  const options = groups.map(g => ({
    label: g.label,
    options: g.options.map(p => ({ value: roamingKey(p), label: roamingLabel(p) })),
  }));

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Paragraph type="secondary" style={{ marginBottom: 0, fontSize: 13 }}>
        Coverage decides in which countries the bundle works. Pick either a{' '}
        <Text strong>region tier</Text> or a <Text strong>single country</Text>. A tier is a set of
        countries in a region: tier 1 is the region's core countries, tier 2 adds more, up to tier 5
        for the whole region; higher tiers cost more. The number after the tier is how many
        countries it covers. Select one and the full list appears below. Single-country coverage is
        the cheapest choice when the vessel stays in one country. Type a region or country name to
        search.
      </Paragraph>
      <Select
        showSearch
        loading={loading}
        value={value ? roamingKey(value) : undefined}
        placeholder={
          loading
            ? 'Loading coverage options…'
            : 'Search a region or country, e.g. "Europe" or "Spain"'
        }
        optionFilterProp="label"
        allowClear
        style={{ width: '100%' }}
        options={options}
        onChange={key => {
          const parsed = key ? parseRoamingKey(key) : null;
          onChange(
            parsed,
            parsed ? profiles.find(p => p.group === parsed.group && p.id === parsed.id) : undefined
          );
        }}
        notFoundContent={loading ? <Spin size="small" /> : 'No profile matches'}
        aria-label="Coverage"
      />
      {recommended && !value && (
        <Text type="secondary" style={{ fontSize: 13 }}>
          Vodafone's default for this bundle is profile {recommended.id} in "{recommended.group}".{' '}
          <a
            onClick={e => {
              e.preventDefault();
              onChange(
                recommended,
                profiles.find(p => p.group === recommended.group && p.id === recommended.id)
              );
            }}
            href="#use-default"
          >
            Use the default
          </a>
        </Text>
      )}
      {selected && <CoverageList group={selected.group} id={selected.id} defaultOpen />}
    </Space>
  );
};

export default RoamingPicker;
